"use client";

import { GET_MATCH_STATS } from "@/backend-graphql/queries/backend-graph-queries";
import { useMatchStore } from "@/store/store";
import { Match } from "@/utils/types";
import { LoadingSpinner } from "@/utils/components/LoadingSpinner";
import {
  NonPlayerRolesMapper,
  playerMovementsIgnorableItems,
  ScoringEventsFractions,
} from "@/utils/constants";
import { useQuery } from "@apollo/client";
import {
  MatchStatsCard,
  MatchTeamName,
  Tab,
  TeamStaff,
  PlayerStaffList,
} from "@nrldigitaltechnology/ui-community";
import { TabOptionProps } from "@nrldigitaltechnology/ui-community/dist/components/Tab/types";
import { AnyProps } from "@nrldigitaltechnology/ui-community/dist/utils/types";
import React, { use, useCallback, useEffect, useState } from "react";

type MatchClientProps = {
  matchId: string;
};

export const MatchClient = ({ matchId }: MatchClientProps) => {
  const { setMatch } = useMatchStore();

  const [matchDetails, setMatchDetails] = useState<Match | null>(null);
  const [scoringEventList, setScoringEventList] = useState<AnyProps[]>([]);

  const [teamStaffList, setTeamStaffList] = useState<AnyProps[]>([]);
  const [playerList, setPlayerList] = useState<AnyProps[]>([]);

  const [selectedTab, setSelectedTab] = useState({
    value: "matchstats",
    label: "Match Stats",
  });

  const { data: matchData, loading: matchLoading } = useQuery(GET_MATCH_STATS, {
    variables: { matchId: Number(matchId) },
    errorPolicy: "all",
  });
  const formatTeamStaffData = useCallback((data: any) => {
    const homePlayers = data?.homeTeamList?.nonplayers || [];
    const awayPlayers = data?.awayTeamList?.nonplayers || [];

    if (!homePlayers && !awayPlayers) {
      return [];
    }

    const nonplayers = [];

    const isHomeLonger = homePlayers.length >= awayPlayers.length;

    const primaryList = isHomeLonger ? homePlayers : awayPlayers;
    const secondaryList = isHomeLonger ? awayPlayers : homePlayers;

    const teamLists = primaryList.map((pItem: AnyProps) => {
      const sItem =
        secondaryList.find((item: AnyProps) => item.role === pItem.role) || {};

      return isHomeLonger
        ? { homePlayerList: pItem, awayPlayerList: sItem }
        : { homePlayerList: sItem, awayPlayerList: pItem };
    });

    for (const secItem of secondaryList) {
      if (
        !teamLists.find(
          (item: AnyProps) => item.homePlayerList.role === secItem.role
        )
      ) {
        teamLists.push({ homePlayerList: {}, awayPlayerList: secItem });
      }
    }

    const reorderedList = [];
    const roleKeys = Object.keys(NonPlayerRolesMapper);

    for (let i = 0; i < roleKeys?.length; i++) {
      for (let j = 0; j < teamLists?.length; j++) {
        if (
          teamLists[j]?.homePlayerList?.role === roleKeys[i] ||
          teamLists[j]?.awayPlayerList?.role === roleKeys[i]
        ) {
          reorderedList.push(teamLists[j]);
        }
      }
    }

    for (let i = 0; i < reorderedList?.length; i++) {
      const arr = [];
      const mapPlayer = (playerObj: any) => {
        return {
          id: playerObj?._id,
          preferredName: playerObj?.preferredName,
          isPrivate: playerObj?.isPrivate,
          name: playerObj?.isPrivate
            ? playerObj.preferredName || "Name Private"
            : `${playerObj?.firstName || ""} ${
                playerObj?.lastName || ""
              }`.trim(),
          role:
            NonPlayerRolesMapper[
              playerObj?.role as keyof typeof NonPlayerRolesMapper
            ]?.toUpperCase() ||
            playerObj?.role?.toUpperCase() ||
            "",
          // avatar:
          //   playerObj?.meta?.avatar ||
          //   (playerObj?.isPrivate || playerObj?.firstName || playerObj?.lastName
          //     ? "/assets/logo/noprofile.png"
          //     : ""),
          shirtNumber: playerObj?.shirtNum,
        };
      };

      const homeObj = reorderedList[i].homePlayerList
        ? mapPlayer(reorderedList[i].homePlayerList)
        : null;
      const awayObj = reorderedList[i]?.awayPlayerList
        ? mapPlayer(reorderedList[i]?.awayPlayerList)
        : null;
      arr.push(homeObj);
      arr.push(awayObj);
      nonplayers.push(arr);
    }
    return nonplayers;
  }, []);

  const formatTeamPlayersData = useCallback((data: any) => {
    // The incoming raw player shape (before mapping)
    type RawPlayer = {
      _id: string;
      preferredName?: string;
      isPrivate: boolean;
      firstName?: string;
      lastName?: string;
      position?: { name?: string; number?: string | number; order?: number };
      shirtNum?: string | number;
      games?: number;
      meta?: { avatar?: string };
      isCaptain?: boolean;
    };

    type Player = {
      id: string;
      preferredName?: string;
      isPrivate: boolean;
      name: string;
      role: string;
      shirtNumber: string | number;
      matches: number;
      avatar: string;
      order?: number;
      isCaptain?: boolean;
    };

    type PlayerPair = [Player | null, Player | null];

    const homePlayers = data?.homeTeamList?.players ?? [];
    const awayPlayers = data?.awayTeamList?.players ?? [];

    if (homePlayers.length === 0 && awayPlayers.length === 0) {
      return [];
    }

    const mapPlayer = (player: RawPlayer): Player | null =>
      player
        ? {
            id: player._id,
            preferredName: player.preferredName,
            isPrivate: player.isPrivate,
            name: player.isPrivate
              ? player.preferredName || "Name Private"
              : `${player.firstName || ""} ${player.lastName || ""}`.trim(),
            role: player.position?.name || "",
            shirtNumber: player.shirtNum || player.position?.number || "",
            matches: player.games ?? 0,
            avatar: player.meta?.avatar || "",
            order: player.position?.order,
            isCaptain: player?.isCaptain,
          }
        : null;

    const groupByRoleAndOrder = (players: RawPlayer[]) => {
      const grouped = new Map<string, Map<number, Player | null>>();
      for (const p of players) {
        const role = p?.position?.name;
        const order = p?.position?.order;
        if (!role || typeof order !== "number") continue;
        if (!grouped.has(role)) {
          grouped.set(role, new Map());
        }
        grouped.get(role)!.set(order, mapPlayer(p));
      }
      return grouped;
    };

    const homeGrouped = groupByRoleAndOrder(homePlayers);
    const awayGrouped = groupByRoleAndOrder(awayPlayers);

    const allRoles = new Set([...homeGrouped.keys(), ...awayGrouped.keys()]);
    const playerGroups: PlayerPair[] = [];

    for (const role of allRoles) {
      const homeRoleMap = homeGrouped.get(role) || new Map();
      const awayRoleMap = awayGrouped.get(role) || new Map();

      const allOrders = new Set<number>([
        ...homeRoleMap.keys(),
        ...awayRoleMap.keys(),
      ]);
      const sortedOrders = Array.from(allOrders).sort((a, b) => a - b);

      for (const order of sortedOrders) {
        const home = homeRoleMap.get(order) || null;
        const away = awayRoleMap.get(order) || null;
        playerGroups.push([home, away]);
      }
    }

    playerGroups.sort((a, b) => {
      const minOrderA = Math.min(
        ...a
          .filter((p): p is Player => p !== null && typeof p.order === "number")
          .map((p) => p.order!),
        Infinity
      );
      const minOrderB = Math.min(
        ...b
          .filter((p): p is Player => p !== null && typeof p.order === "number")
          .map((p) => p.order!),
        Infinity
      );

      if (minOrderA !== minOrderB) return minOrderA - minOrderB;

      const roleA = a[0]?.role || a[1]?.role || "";
      const roleB = b[0]?.role || b[1]?.role || "";
      return roleA.localeCompare(roleB);
    });

    return playerGroups;
  }, []);

  useEffect(() => {
    if (matchData?.match) {
      setMatch(matchData?.match);
      setMatchDetails(matchData?.match);
      if (selectedTab?.value === "teamstaff") {
        const stList = formatTeamStaffData(matchData?.match) || [];
        setTeamStaffList(stList);
      }
      if (selectedTab?.value === "players") {
        const pList = formatTeamPlayersData(matchData?.match) || [];
        setPlayerList(pList);
      }
    }
  }, [matchData, selectedTab?.value]);

  const pickFractionalList = (eventKeyValue: Record<string, AnyProps>) => {
    const fractionalList: AnyProps = [];
    Object.entries(ScoringEventsFractions).forEach(([key, value]) => {
      const homeConversion0 =
        eventKeyValue
          .get(value[0])
          ?.filter((item: AnyProps) => item.isHomeTeam) ?? [];
      const homeConversion1 =
        eventKeyValue
          .get(value[1])
          ?.filter((item: AnyProps) => item.isHomeTeam) ?? [];

      const awayConversion0 =
        eventKeyValue
          .get(value[0])
          ?.filter((item: AnyProps) => !item.isHomeTeam) ?? [];
      const awayConversion1 =
        eventKeyValue
          .get(value[1])
          ?.filter((item: AnyProps) => !item.isHomeTeam) ?? [];

      if (key && value) {
        const obj = {
          type: key,
          applicableIds: value,
          totHomeConversion: [...homeConversion0, ...homeConversion1].length,
          totAwayConversion: [...awayConversion0, ...awayConversion1].length,
        };
        fractionalList.push(obj);
      }
    });

    return fractionalList;
  };

  const reformateEventName = (val: string) => {
    switch (val) {
      case "Try":
        return "Tries";
      default:
        return val;
    }
  };

  useEffect(() => {
    if (!matchDetails?.scoringEvents && !matchDetails?.playerMovements) return;
    const scoreEventList = matchDetails.scoringEvents || [];

    if (scoreEventList.length > 0) {
      const keys = scoreEventList?.map((item: AnyProps) => item.type);
      const uniqueKeys = Array.from(new Set(keys));
      const scoreEventDict = new Map();

      uniqueKeys.forEach((key) => {
        const eventValue = matchDetails.scoringEvents?.filter(
          (event: AnyProps) => event.type === key
        );
        scoreEventDict.set(key, eventValue);
      });

      let fractionalList: AnyProps = [];
      fractionalList = pickFractionalList(scoreEventDict);

      const seList: AnyProps = [];

      scoreEventDict?.forEach((value, key) => {
        if (key === 2 || key === 68) return;

        const homeTeamList = value.filter((fi: AnyProps) => fi.isHomeTeam);
        const awayTeamList = value.filter((fi: AnyProps) => !fi.isHomeTeam);

        const fractionalObject =
          fractionalList?.find((item: AnyProps) =>
            item.applicableIds.includes(Number(key))
          ) || null;
        let totHomeVal = fractionalObject
          ? fractionalObject?.totHomeConversion
          : 0;
        let totAwayVal = fractionalObject
          ? fractionalObject?.totAwayConversion
          : 0;

        const redefinedObjPlayers = (obj: any) => {
          return {
            ...obj,
            ...(obj?.meta?.player?.isPrivate
              ? { firstName: "Name Private", lastName: "" }
              : obj?.meta?.player?.preferredName
              ? { firstName: obj?.meta?.player?.preferredName, lastName: "" }
              : obj?.meta?.player?.firstName || obj?.player?.meta?.lastName
              ? {
                  firstName: obj?.meta?.player?.firstName,
                  lastName: obj?.meta?.player?.lastName,
                }
              : { firstName: "-", lastName: "" }),
            shirtNum: obj?.min + "'",
          };
        };

        const obj = {
          type: "scoring_event",
          eventType: key,
          eventName:
            homeTeamList.length > 0 || awayTeamList.length > 0
              ? reformateEventName(value[0].name)
              : value[0].name,
          homeEventValue:
            totHomeVal > 0
              ? `${homeTeamList.length}/${totHomeVal}`
              : homeTeamList.length === 0
              ? 0
              : homeTeamList.length,
          awayEventValue:
            totAwayVal > 0
              ? `${awayTeamList.length}/${totAwayVal}`
              : awayTeamList.length === 0
              ? 0
              : awayTeamList.length,
          homePlayers: homeTeamList.map((item: AnyProps) => {
            return redefinedObjPlayers(item);
          }),

          awayPlayers: awayTeamList.map((item: AnyProps) => {
            return redefinedObjPlayers(item);
          }),
        };
        if (obj.awayEventValue !== 0 || obj.homeEventValue !== 0) {
          seList?.push(obj);
        }
        setScoringEventList(seList);
      });
    }

    const playerList = matchDetails.playerMovements || [];
    if (playerList.length > 0) {
      const movementsKeys = playerList?.map((item: AnyProps) => item.type);

      const filteredKeys = movementsKeys.filter(
        (item) =>
          !Object.keys(playerMovementsIgnorableItems).includes(item.toString())
      );

      const uniqueMovementKeys = Array.from(new Set(filteredKeys));
      const movementsKeyValue = new Map();

      uniqueMovementKeys.forEach((pKey: AnyProps) => {
        const movementValue = matchDetails.playerMovements?.filter(
          (event: AnyProps) => event.type === pKey
        );
        movementsKeyValue.set(pKey, movementValue);
      });

      const movList: AnyProps = [];
      movementsKeyValue?.forEach((value, key) => {
        const homeTeamList = value.filter((fi: AnyProps) => fi.isHomeTeam);
        const awayTeamList = value.filter((fi: AnyProps) => !fi.isHomeTeam);

        const homePlayers: AnyProps[] = [];
        const awayPlayers: AnyProps[] = [];

        const redefinedObjOn = (obj: any) => {
          return {
            ...obj,
            ...(obj?.meta?.on?.isPrivate
              ? { firstName: "Name Private", lastName: "" }
              : obj?.meta?.on?.preferredName
              ? { firstName: obj?.meta?.on?.preferredName, lastName: "" }
              : obj?.meta?.on?.firstName || obj?.player?.meta?.lastName
              ? {
                  firstName: obj?.meta?.on?.firstName,
                  lastName: obj?.meta?.on?.lastName,
                }
              : { firstName: "-", lastName: "" }),
            shirtNum: obj?.min + "'",
          };
        };

        const redefinedObjOff = (obj: any) => {
          return {
            ...obj,
            ...(obj?.meta?.off?.isPrivate
              ? { firstName: "Name Private", lastName: "" }
              : obj?.meta?.off.preferredName
              ? { firstName: obj?.meta?.off?.preferredName, lastName: "" }
              : obj?.meta?.off?.firstName || obj?.player?.meta?.lastName
              ? {
                  firstName: obj?.meta?.off?.firstName,
                  lastName: obj?.meta?.off?.lastName,
                }
              : { firstName: "-", lastName: "" }),
            shirtNum: obj?.min + "'",
          };
        };

        homeTeamList &&
          homeTeamList?.forEach((item: AnyProps) => {
            if (item?.meta?.on?._id) {
              const onObj = redefinedObjOn(item);

              homePlayers.push(onObj);
            }
            if (item?.meta?.off?._id) {
              const offObj = redefinedObjOff(item);
              homePlayers.push(offObj);
            }
          });

        awayTeamList?.forEach((item: AnyProps) => {
          if (item?.meta?.on?._id) {
            const onObj = redefinedObjOn(item);
            awayPlayers.push(onObj);
          }
          if (item?.meta?.off?._id) {
            const offObj = redefinedObjOff(item);
            awayPlayers.push(offObj);
          }
        });

        const obj = {
          type: "player_movement",
          eventType: key,
          eventName: value[0].name,
          homeEventValue: homeTeamList?.length || 0,
          awayEventValue: awayTeamList?.length || 0,
          homePlayers,
          awayPlayers,
        };
        movList?.push(obj);
      });
      setScoringEventList((prev) => [...prev, ...movList]);
    }
  }, [matchDetails]);

  const onChangeTab = (item: TabOptionProps) => {
    setSelectedTab(item);
  };

  return (
    <>
      <div className="w-full bg-gray-100 flex flex-col items-center">
        <Tab
          options={[
            {
              value: "matchstats",
              label: "Match Stats",
            },
            {
              value: "players",
              label: "Players",
            },
            {
              value: "teamstaff",
              label: "Team Staff",
            },
          ]}
          tab={selectedTab}
          onChangeTab={onChangeTab}
          variant="underline"
          customStyles={{
            containerStyle: "w-full bg-white rounded-md px-[16px] mt-6",
            tabBtnStyle:
              "data-[selected=true]:border-b-[3px] mb-[-1px] py-[10px] px-[16px]",
          }}
        />

        {selectedTab.value === "matchstats" ? (
          <>
            {matchDetails && matchDetails?.scoringEvents?.length > 0 ? (
              <div className="w-full bg-white flex flex-col my-4 gap-10 justify-center items-center rounded-md my-5">
                <div className="w-full">
                  <MatchTeamName
                    teamA={{
                      name: matchDetails?.homeTeam?.name || "",
                      grade: "",
                    }}
                    teamB={{
                      name: matchDetails?.awayTeam?.name || "",
                      grade: "",
                    }}
                  />
                </div>

                {scoringEventList.map((item: AnyProps) => (
                  <MatchStatsCard key={item.eventType} scoreEventData={item} />
                ))}
              </div>
            ) : (
              <>
                {matchLoading ? (
                  <LoadingSpinner text="Loading" />
                ) : (
                  <div className="w-full bg-white flex flex-col my-4 gap-10 justify-center items-center rounded-md py-5">
                    <p>No Match Stats</p>
                  </div>
                )}
              </>
            )}
          </>
        ) : selectedTab.value === "players" ? (
          <>
            {playerList?.length > 0 ? (
              <div className="bg-gray-100  w-full flex flex-col gap-5 mb-10 rounded-md pt-4">
                {playerList.map((player: any[], index: number) => (
                  <PlayerStaffList key={index} staff={player} />
                ))}
              </div>
            ) : (
              <div className="w-full bg-white flex flex-row justify-center items-center rounded-md py-5 my-5">
                <p>No Players</p>
              </div>
            )}
          </>
        ) : selectedTab.value === "teamstaff" ? (
          <>
            {teamStaffList?.length > 0 ? (
              <div className=" bg-gray-100 w-full flex flex-col gap-5 items-center my-5 rounded-md">
                {teamStaffList?.map((item, index) => (
                  <div className="w-full" key={`${item._id}_${index}`}>
                    <TeamStaff staff={item} />
                  </div>
                ))}
              </div>
            ) : (
              <div className=" w-full bg-white flex flex-row justify-center items-center rounded-md py-5 my-5">
                <p>No Team Staff</p>
              </div>
            )}
          </>
        ) : null}
      </div>
    </>
  );
};
